import React, { useState, useMemo } from "react";
import { type NodeProps, type Node, useNodes, useEdges } from "@xyflow/react";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";
import syntaxHighlight from "../../utils/jsonViewer";

export const ErrorCatcherNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const nodes = useNodes();
  const edges = useEdges();

  // Get source nodes connected via error handles
  const errorSourceNodes = useMemo(() => {
    const incomingEdges = edges.filter(
      (edge) => edge.target === id && edge.sourceHandle === "error",
    );
    return incomingEdges
      .map((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        return sourceNode
          ? {
              nodeId: sourceNode.id,
              nodeName: sourceNode.data?.name || sourceNode.id,
              nodeType: sourceNode.type || "unknown",
            }
          : null;
      })
      .filter(Boolean);
  }, [id, nodes, edges]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;
  const isConfigured = errorSourceNodes.length > 0;

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Error Catcher"
      icon={<ShieldAlert className="w-4 h-4 text-rose-700" />}
      nodeDocsType="ERROR-CATCHER"
      themeColor="rose"
      configureLabel="View Error Details"
      isConfigured={isConfigured}
      hasSourceHandle={true}
      validationErrors={[]}
      statusText={{
        configured: "Connected",
        notConfigured: "Not Connected",
        running: "Executing...",
        success: "Error Caught",
        error: "Execution Failed",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      {/* Diamond overlay on the default left target handle */}
      <div className="absolute -left-[5px] top-1/2 -translate-y-1/2 z-20 pointer-events-none">
        <div className="w-2.5 h-2.5 rotate-45 bg-gradient-to-br from-red-400 to-red-600 border border-white" style={{ boxShadow: "0 0 0 1.5px rgba(239, 68, 68, 0.2), 0 1px 4px rgba(239, 68, 68, 0.3)" }} />
      </div>

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        title="Error Catcher - Error details"
        icon={<ShieldAlert className="w-5 h-5 text-rose-600" />}
        themeColor="rose"
        nodeDocsType="ERROR-CATCHER"
        validationStatus={{ isValid: true, errors: [] }}
        nodeData={data}
        nodeIcon={<ShieldAlert className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No errors caught yet"
        saveLabel="Close"
        renderLeftSidebar={() => (
          <aside className="w-[420px] shrink-0 bg-gray-50/50 flex flex-col h-full overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200">
              <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Error Sources
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-3 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
              {errorSourceNodes.length > 0 ? (
                <div className="space-y-2">
                  {errorSourceNodes.map((node: any) => (
                    <div
                      key={node.nodeId}
                      className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                        <span className="text-xs font-medium text-gray-900">
                          {node.nodeName}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-500 mt-1 block">
                        Type: {node.nodeType}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic p-3 bg-white rounded-lg border border-dashed border-gray-300 text-center">
                  No error sources connected yet. Connect the error output of
                  other nodes to this Error Catcher.
                </div>
              )}
            </div>
          </aside>
        )}
        renderOutputsSidebar={() => (
          <aside className="w-[420px] shrink-0 bg-gray-50/50 flex flex-col h-full overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-200">
              <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                OUTPUT
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full">
              {hasOutputs ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <pre
                    className="p-3 text-xs font-mono break-all whitespace-pre-wrap leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: syntaxHighlight(data.outputs),
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                  <div className="w-14 h-14 mb-3 bg-gray-100 rounded-xl flex items-center justify-center text-gray-300 border border-dashed border-gray-300">
                    <ShieldAlert className="w-6 h-6" />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-1">
                    No errors caught yet
                  </h4>
                  <p className="text-[10px] text-gray-400 leading-relaxed">
                    Run the workflow to see caught errors
                  </p>
                </div>
              )}
            </div>
          </aside>
        )}
      >
        {/* Center: Error info */}
        <div className="space-y-5">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-900 mb-1">
                  Error Catcher Node
                </h4>
                <p className="text-sm text-red-700">
                  This node catches errors from connected nodes via their error
                  output handles. When a connected node fails, the error details
                  are captured here and the workflow can continue through this
                  node's output.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              How to Use
            </h4>
            <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
              <li>
                Connect the{" "}
                <span className="font-medium text-red-600">error handle</span>{" "}
                of any node to this Error Catcher
              </li>
              <li>
                When the source node encounters an error, it will be routed here
              </li>
              <li>
                The error details will be available in this node's outputs
              </li>
              <li>
                Connect this node's output to continue the workflow after
                catching the error
              </li>
            </ul>
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default ErrorCatcherNode;
